import { useState, type FormEvent } from 'react'
import type { Group, CreateGroupInput, UpdateGroupInput } from '../model/contracts'
import { Button } from '../../../shared/ui/Button'

interface GroupFormProps {
  group?: Group
  availableSources: Array<{ id: string; name: string }>
  availablePages: Array<{ id: string; name: string }>
  onSubmit: (data: CreateGroupInput | UpdateGroupInput) => void
  onCancel?: () => void
}

export function GroupForm({
  group,
  availableSources,
  availablePages,
  onSubmit,
  onCancel,
}: GroupFormProps) {
  const [name, setName] = useState(group?.name || '')
  const [description, setDescription] = useState(group?.description || '')
  const [selectedSourceIds, setSelectedSourceIds] = useState<string[]>(
    group?.sourceIds || [],
  )
  const [selectedPageIds, setSelectedPageIds] = useState<string[]>(group?.pageIds || [])

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return

    if (group) {
      onSubmit({
        id: group.id,
        name,
        description,
        sourceIds: selectedSourceIds,
        pageIds: selectedPageIds,
      })
    } else {
      onSubmit({
        name,
        description,
        sourceIds: selectedSourceIds,
        pageIds: selectedPageIds,
      })
    }
  }

  const toggleSource = (id: string) => {
    setSelectedSourceIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    )
  }

  const togglePage = (id: string) => {
    setSelectedPageIds((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id],
    )
  }

  return (
    <form
      onSubmit={handleSubmit}
      style={{ display: 'flex', flexDirection: 'column', gap: 16 }}
    >
      <div>
        <label
          htmlFor="group-name"
          style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}
        >
          Group Name *
        </label>
        <input
          id="group-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
          className="bg-input-overlay"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid var(--panel-border)',
            color: 'var(--text)',
            fontSize: 14,
          }}
          placeholder="Enter group name"
        />
      </div>

      <div>
        <label
          htmlFor="group-description"
          style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}
        >
          Description
        </label>
        <textarea
          id="group-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          className="bg-input-overlay"
          style={{
            width: '100%',
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid var(--panel-border)',
            color: 'var(--text)',
            fontSize: 14,
            resize: 'vertical',
            fontFamily: 'inherit',
          }}
          placeholder="Optional description"
        />
      </div>

      {availableSources.length > 0 && (
        <div>
          <span
            style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}
          >
            Select Sources
          </span>
          <div
            className="bg-input-overlay"
            style={{
              padding: 12,
              borderRadius: 8,
              border: '1px solid var(--panel-border)',
              maxHeight: 150,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {availableSources.map((source) => (
              <label
                key={source.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedSourceIds.includes(source.id)}
                  onChange={() => toggleSource(source.id)}
                  style={{ cursor: 'pointer' }}
                />
                {source.name}
              </label>
            ))}
          </div>
        </div>
      )}

      {availablePages.length > 0 && (
        <div>
          <span
            style={{ display: 'block', marginBottom: 8, fontSize: 14, fontWeight: 500 }}
          >
            Select Pages
          </span>
          <div
            className="bg-input-overlay"
            style={{
              padding: 12,
              borderRadius: 8,
              border: '1px solid var(--panel-border)',
              maxHeight: 150,
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
              gap: 8,
            }}
          >
            {availablePages.map((page) => (
              <label
                key={page.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: 'pointer',
                  fontSize: 14,
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedPageIds.includes(page.id)}
                  onChange={() => togglePage(page.id)}
                  style={{ cursor: 'pointer' }}
                />
                {page.name}
              </label>
            ))}
          </div>
        </div>
      )}

      <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
        {onCancel && (
          <Button type="button" variant="neutral" onClick={onCancel}>
            Cancel
          </Button>
        )}
        <Button type="submit">{group ? 'Update' : 'Create'} Group</Button>
      </div>
    </form>
  )
}
